import { polarToXY, readsMirrored } from '@circle/shared';
import type { Fragment, MonthCtx } from '../types';
import { annulusPath, arcPath, n, svgEl } from '../../core/svg';
import { metricsAt, W } from './metrics';

/**
 * A month is a faint annular wedge (which doubles as its hit area), a boundary
 * rule at its leading edge, and its name set along an arc outside the ring.
 * Months whose label would hang upside down get a reversed path so it stays
 * right-way-up.
 *
 * Which months those are depends on where the ring is *turned to*, not on where
 * the month was drawn: bringing July to the top used to leave its label
 * mirrored, because the decision was made from the world angle alone. The scene
 * hands in the current rotation and re-renders a month when its answer changes.
 */
export const monthFragment: Fragment<MonthCtx> = (ctx, state) => {
  const M = metricsAt(ctx.radius);
  const g = svgEl('g', { 'data-frag': 'month' });
  const mid = (ctx.startAngle + ctx.endAngle) / 2;
  const flip = readsMirrored(mid, ctx.rotation ?? 0);
  const pathId = `month-arc-${ctx.ringId}-${ctx.year}-${ctx.index}`;

  g.append(
    svgEl('path', {
      d: annulusPath(M.bandInner, M.bandOuter, ctx.startAngle, ctx.endAngle),
      fill: state.hover || state.selected
        ? 'var(--month-fill-hover)'
        : ctx.index % 2 === 0
          ? 'var(--month-fill-alt)'
          : 'var(--month-fill)',
      stroke: 'none',
      'pointer-events': 'fill',
    }),
  );

  // Leading-edge rule, drawn across the band.
  const a = ctx.startAngle;
  const p0 = polarToXY(a, M.bandInner);
  const p1 = polarToXY(a, M.bandOuter);
  g.append(
    svgEl('line', {
      x1: n(p0.x),
      y1: n(p0.y),
      x2: n(p1.x),
      y2: n(p1.y),
      stroke: 'var(--month-rule)',
      'stroke-width': W.monthBoundary,
      'vector-effect': 'non-scaling-stroke',
      'pointer-events': 'none',
    }),
  );

  // A ring too small to hold a month name keeps the wedge and the rule — the
  // months are still there to be seen and clicked — and drops the label rather
  // than ringing itself with unreadable type.
  if (ctx.compact) return g;

  const defs = svgEl('defs');
  defs.append(
    svgEl('path', {
      id: pathId,
      fill: 'none',
      d: flip
        ? arcPath(M.labelArc, ctx.endAngle, ctx.startAngle)
        : arcPath(M.labelArc, ctx.startAngle, ctx.endAngle),
    }),
  );
  g.append(defs);

  const text = svgEl('text', {
    fill: state.hover || state.selected ? 'var(--month-label-hover)' : 'var(--month-label)',
    'font-size': M.labelSize,
    'font-weight': state.selected ? 600 : 400,
    'letter-spacing': 7,
    'pointer-events': 'none',
    // Glyphs grow outward from a clockwise path and inward from a reversed one,
    // so the flipped labels need a nudge to sit in the same band.
    dy: flip ? 26 : 0,
  });
  const textPath = svgEl('textPath', {
    href: `#${pathId}`,
    startOffset: '50%',
    'text-anchor': 'middle',
  });
  textPath.textContent = ctx.name.toUpperCase();
  text.append(textPath);
  g.append(text);

  return g;
};
